/**
 * useAccountsData — Phase 4B.
 *
 * Unified reader/writer over the three tables that Accounts now
 * manages:
 *
 *   \u2022 accounts          — Cash-side rows (bank / credit_card / wallet /
 *                        cash / other). `type='credit_card'` rows are
 *                        surfaced under Liabilities in the new UI.
 *   \u2022 wealth_holdings   — Investment rows. Each row is one holding
 *                        (a mutual fund, a stock, a deposit, etc).
 *   \u2022 liabilities       — Non-card debts (home loan, car loan, etc).
 *
 * Everything is normalised into a single `NormalisedAccount` shape so
 * the redesigned UI can stay category-oblivious:
 *
 *   {\n *     id, source_table, category, type,\n *     name, institution, currency, balance,\n *     last_updated, account_number,\n *     interest_rate, credit_limit, next_payment_amount, next_payment_date,\n *     minimum_payment, original_amount,\n *     holdings, day_change,\n *     raw   — the untouched row (for edit dialogs)\n *   }
 *
 *   \u2022 category \u2208 { 'cash', 'investments', 'liabilities' }\n *   \u2022 For liabilities, balance is stored negative (matches Figma\n *     convention used by the rest of the Accounts UI).
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { setAccountBalanceManually } from '@/lib/accountBalance';
import { useAuth } from '@/context/AuthContext';
import { readCache, writeCache } from '@/lib/pageCache';

const CACHE_KEY = 'accounts-normalised';

// Adding, editing or deleting an account changes the cash and debt plans too.
// They listen for this; without it the account list and the planning strip
// disagreed until the next page load.
const notifyDependents = () => {
  window.dispatchEvent(new CustomEvent('accounts:changed'));
};

/* ------------------------------------------------------------------ */
/* Type / category mapping                                            */
/* ------------------------------------------------------------------ */

/** accounts.type → UI category. Credit cards live under Liabilities. */
const ACCOUNT_TYPE_CATEGORY = {
  bank:        'cash',
  savings:     'cash',
  chequing:    'cash',
  checking:    'cash',
  current:     'cash',
  money_market:'cash',
  wallet:      'cash',
  cash:        'cash',
  prepaid:     'cash',
  mobile_money:'cash',
  e_wallet:    'cash',
  other:       'cash',
  credit_card: 'liabilities',
  // legacy back-compat
  demat:       'investments',
  investment:  'investments',
  deposit:     'investments',
};

/** Human-friendly type shown in row meta. */
const ACCOUNT_TYPE_LABEL = {
  bank:        'Bank account',
  savings:     'Savings account',
  chequing:    'Current account',
  checking:    'Checking account',
  current:     'Current account',
  money_market:'Money market',
  wallet:      'Digital wallet',
  e_wallet:    'Digital wallet',
  mobile_money:'Mobile money',
  prepaid:     'Prepaid balance',
  cash:        'Physical cash',
  other:       'Other stored value',
  credit_card: 'credit-card',    // matches the Figma detail-view branching
  // wealth_holdings kinds
  mutual_fund: 'Mutual Funds',
  stock:       'Brokerage',
  etf:         'Brokerage',
  bond:        'Bond',
  gold:        'Gold',
  deposit:     'Deposit',
  // liabilities kinds
  home_loan:     'mortgage',
  mortgage:      'mortgage',
  car_loan:      'loan',
  personal_loan: 'loan',
  student_loan:  'loan',
  credit_line:   'loan',
};

/** Category-aware `type` returned in the normalised shape. Detail views
 *  branch off this. */
const NORMALISED_TYPE = {
  // cash
  // Every deposit-taking bank account groups under the 'savings' sub-tab; the
  // human label (Savings vs Current) comes from `subtype_label`.
  bank:   'savings', savings: 'savings',
  chequing: 'savings', checking: 'savings', current: 'savings',
  money_market: 'savings',
  wallet: 'wallet', cash: 'wallet', other: 'wallet',
  e_wallet: 'wallet', mobile_money: 'wallet', prepaid: 'wallet',
  // liabilities
  credit_card:   'credit-card',
  home_loan:     'mortgage',
  mortgage:      'mortgage',
  car_loan:      'loan',
  personal_loan: 'loan',
  student_loan:  'loan',
  credit_line:   'loan',
  // investments
  mutual_fund: 'funds',
  stock:       'brokerage',
  etf:         'brokerage',
  bond:        'other-investment',
  gold:        'other-investment',
  deposit:     'other-investment',
};

/* ------------------------------------------------------------------ */
/* Normalisers — one per source table                                 */
/* ------------------------------------------------------------------ */

function normaliseCashAccount(row) {
  const category = ACCOUNT_TYPE_CATEGORY[row.type] || 'cash';
  const rawBal = Number(row.balance || 0);
  // credit_card rows carry a positive `balance` for money owed. In the
  // new UI liabilities are negative.
  const balance = category === 'liabilities' ? -Math.abs(rawBal) : rawBal;
  return {
    id: row.id,
    source_table: 'accounts',
    category,
    type: NORMALISED_TYPE[row.type] || 'wallet',
    subtype_label: ACCOUNT_TYPE_LABEL[row.type] || row.type,
    name: row.name || 'Unnamed account',
    // Phase 86.K — institution is optional and user-entered. We never guess it
    // from the name; an empty value means "not told us yet".
    institution: row.institution || null,
    is_joint: !!row.is_joint,
    balance_manual: !!row.balance_manual,
    currency: row.currency || 'INR',
    balance,
    last_updated: row.balance_as_of_date || row.updated_at || row.created_at,
    account_number: row.account_number || null,
    credit_limit:  row.credit_limit || null,
    // liability defaults — populated only if the row genuinely has them
    interest_rate: row.interest_rate || null,
    next_payment_amount: row.next_payment_amount || null,
    next_payment_date:   row.next_payment_date   || null,
    minimum_payment:     row.minimum_payment     || null,
    original_amount:     null,
    holdings:  [],
    day_change: null,
    raw: row,
  };
}

function normaliseWealthHolding(row) {
  return {
    id: row.id,
    source_table: 'wealth_holdings',
    category: 'investments',
    type: NORMALISED_TYPE[row.kind] || 'other-investment',
    subtype_label: ACCOUNT_TYPE_LABEL[row.kind] || row.kind || 'Investment',
    name: row.name || 'Holding',
    institution: row.name || row.symbol_or_isin || '',
    currency: row.currency || 'INR',
    balance: Number(row.value || 0),
    last_updated: row.updated_at || row.created_at,
    account_number: row.symbol_or_isin || null,
    interest_rate: row.interest_rate || null,
    credit_limit: null,
    next_payment_amount: null,
    next_payment_date:   null,
    minimum_payment:     null,
    original_amount:     row.invested_value || row.principal_amount || null,
    // For a single-row holding, we surface *itself* as the holding to
    // keep the InvestmentDetail table happy.
    holdings: [
      {
        name: row.name || row.symbol_or_isin || 'Holding',
        ticker: row.symbol_or_isin || null,
        units: row.quantity != null ? Number(row.quantity) : null,
        price: row.last_known_nav != null ? Number(row.last_known_nav) : null,
        value: Number(row.value || 0),
        return_pct: row.invested_value && row.invested_value > 0
          ? Number((((row.value || 0) - row.invested_value) / row.invested_value * 100).toFixed(2))
          : null,
        type: row.kind === 'mutual_fund' ? 'fund' : 'stock',
        // Phase 84.22.9 — plumb `last_priced_at` + `auto_update` so the
        // InvestmentDetail row can render a "Last priced ·" chip and
        // grey out the price for manually-priced holdings.
        last_priced_at: row.last_priced_at || null,
        auto_update: row.auto_update !== false,   // default true (matches DB default)
        currency: (row.currency || '').toUpperCase() || null,
      },
    ],
    day_change: null,
    // deposit-specific fields, useful for the detail view
    deposit_subtype: row.deposit_subtype || null,
    principal_amount: row.principal_amount || null,
    monthly_contribution: row.monthly_contribution || null,
    start_date: row.start_date || null,
    maturity_date: row.maturity_date || null,
    kind: row.kind,
    raw: row,
  };
}

function normaliseLiability(row) {
  const outstanding = Number(row.outstanding || 0);
  return {
    id: row.id,
    source_table: 'liabilities',
    category: 'liabilities',
    type: NORMALISED_TYPE[row.kind] || 'loan',
    subtype_label: ACCOUNT_TYPE_LABEL[row.kind] || row.kind || 'Loan',
    name: row.name || 'Loan',
    institution: row.name || '',
    currency: row.currency || 'INR',
    balance: -Math.abs(outstanding),
    last_updated: row.updated_at || row.created_at,
    account_number: null,
    interest_rate: row.interest_rate || null,
    credit_limit: null,
    next_payment_amount: row.emi_amount || null,
    next_payment_date:   row.next_payment_date || null,
    minimum_payment:     row.emi_amount || null,
    original_amount:     row.principal_amount || outstanding,
    holdings: [],
    day_change: null,
    kind: row.kind,
    raw: row,
  };
}

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

export default function useAccountsData() {
  const { user } = useAuth();
  const uid = user?.id;
  // The account list is factual and cheap — show the last known rows on the
  // first frame so "What you have" / "What you owe" never starts empty.
  const [accounts, setAccounts] = useState(() => readCache(uid, CACHE_KEY) || []);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [cash, wealth, liabs] = await Promise.all([
        supabase.from('accounts').select('*').order('created_at', { ascending: false }),
        supabase.from('wealth_holdings').select('*').order('updated_at', { ascending: false }),
        supabase.from('liabilities').select('*').order('updated_at', { ascending: false }),
      ]);

      const errs = [cash.error, wealth.error, liabs.error].filter(Boolean);
      if (errs.length === 3) throw errs[0]; // hard fail only if everything failed

      const rows = [];
      (cash.data   || []).forEach((r) => rows.push(normaliseCashAccount(r)));
      (wealth.data || []).forEach((r) => rows.push(normaliseWealthHolding(r)));
      (liabs.data  || []).forEach((r) => rows.push(normaliseLiability(r)));

      setAccounts(rows);
      writeCache(uid, CACHE_KEY, rows);
    } catch (e) {
      console.warn('[useAccountsData] load failed:', e);
      setError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  // Listen for global refresh nudges (e.g. after /api/wealth/refresh-prices).
  useEffect(() => {
    const onRefresh = () => { load(); };
    window.addEventListener('accounts:refresh', onRefresh);
    return () => window.removeEventListener('accounts:refresh', onRefresh);
  }, [load]);

  /* ---------- Mutations ---------- */

  const createRow = useCallback(async (category, payload, userId) => {
    const table = category === 'cash'
      ? 'accounts'
      : category === 'investments' ? 'wealth_holdings' : 'liabilities';
    // Phase 84.22.11 — `source` is a column on `wealth_holdings` and
    // `liabilities` (added in Phase 60B for provenance tracking) but
    // NOT on `accounts`. Including it on an accounts insert triggers
    // PostgREST error PGRST204 ("Could not find the 'source' column of
    // 'accounts' in the schema cache") the moment a real user creates
    // a cash account. Only stamp `source: 'manual'` on the two tables
    // that actually have the column.
    const row = table === 'accounts'
      ? { user_id: userId, ...payload }
      : { user_id: userId, source: 'manual', ...payload };
    const { error: err } = await supabase.from(table).insert(row);
    if (err) throw err;
    await load();
    notifyDependents();
  }, [load]);

  const updateRow = useCallback(async (sourceTable, id, patch) => {
    // Never let user_id / source be patched here — they're immutable.
    const { user_id: _u, source: _s, ...clean } = patch || {};
    const { error: err } = await supabase.from(sourceTable).update(clean).eq('id', id);
    if (err) throw err;
    await load();
    notifyDependents();
  }, [load]);

  const deleteRow = useCallback(async (sourceTable, id) => {
    const { error: err } = await supabase.from(sourceTable).delete().eq('id', id);
    if (err) throw err;
    await load();
    notifyDependents();
  }, [load]);

  const setBalanceManually = useCallback(async (account, newBalance) => {
    if (account.source_table === 'accounts') {
      const r = await setAccountBalanceManually({
        account_id: account.id,
        balance: Number(newBalance),
      });
      if (!r?.ok) {
        // Fallback for environments where balance_manual / balance_as_of_date
        // haven't been migrated yet — just set the naked balance.
        const msg = String(r?.error || '');
        if (/column .* does not exist|PGRST204/i.test(msg)) {
          const { error: err2 } = await supabase
            .from('accounts')
            .update({ balance: Number(newBalance) })
            .eq('id', account.id);
          if (err2) throw new Error(err2.message);
        } else {
          throw new Error(r?.error || 'Could not set balance');
        }
      }
    } else if (account.source_table === 'wealth_holdings') {
      const { error: err } = await supabase
        .from('wealth_holdings')
        .update({ value: Number(newBalance), updated_at: new Date().toISOString() })
        .eq('id', account.id);
      if (err) throw err;
    } else if (account.source_table === 'liabilities') {
      const { error: err } = await supabase
        .from('liabilities')
        .update({ outstanding: Math.abs(Number(newBalance)), updated_at: new Date().toISOString() })
        .eq('id', account.id);
      if (err) throw err;
    }
    await load();
    notifyDependents();
  }, [load]);

  return {
    accounts,
    loading,
    error,
    reload: load,
    createRow,
    updateRow,
    deleteRow,
    setBalanceManually,
  };
}
